/**
 * MONANI CRO
 * ----------------------------------------------------------------------------
 * One small module for every conversion component added to the Horizon theme:
 *   - <monani-mini-card>     compact product card with option selects + in-place add
 *   - <monani-recs>          lazy loader for cart upsells and the PDP cross-sell
 *   - <monani-ship-note>     contextual free shipping line on the product page
 *   - <monani-pack-builder>  "Build your own 3-pack" page
 *   - reward gift button, rotation links, analytics
 *
 * It reuses Horizon's own cart plumbing: items are added through the Ajax Cart API with
 * the cart sections bundled in, then a CartAddEvent is dispatched so the drawer, cart page
 * and cart icon update exactly as they do for the theme's own Add to cart button.
 *
 * No dependencies. Settings come from snippets/monani-cro-config.liquid.
 */

import { CartAddEvent, ThemeEvents } from '@theme/events';

/* -------------------------------------------------------------------------- */
/* Config                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * @typedef {Object} CroConfig
 * @property {boolean} shipEligible
 * @property {number} tier1
 * @property {number} tier2
 * @property {number} tier3
 * @property {number} cartTotal
 * @property {number} cartCount
 * @property {string} currency
 * @property {string} moneyFormat
 * @property {number} limitDesktop
 * @property {number} limitMobile
 * @property {boolean} analytics
 * @property {boolean} debug
 * @property {string} template
 * @property {{cartAdd: string, cart: string, recommendations: string, root: string}} routes
 */

/** @returns {CroConfig} */
function readConfig() {
  const defaults = {
    shipEligible: false,
    tier1: 0,
    tier2: 0,
    tier3: 0,
    cartTotal: 0,
    cartCount: 0,
    currency: 'USD',
    moneyFormat: '${{amount}}',
    limitDesktop: 3,
    limitMobile: 2,
    analytics: true,
    debug: false,
    template: '',
    routes: { cartAdd: '/cart/add.js', cart: '/cart.js', recommendations: '/recommendations/products', root: '/' },
  };

  try {
    const el = document.getElementById('monani-cro-config');
    return el ? { ...defaults, ...JSON.parse(el.textContent || '{}') } : defaults;
  } catch (error) {
    console.error('[MONANI CRO] Could not read config', error);
    return defaults;
  }
}

const config = readConfig();
const mobileQuery = window.matchMedia('(max-width: 749px)');

/** Live cart state, kept current from cart:update events. */
const cartState = { total: config.cartTotal, count: config.cartCount };

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Formats cents using the shop money format. Whole amounts drop the ".00".
 * @param {number} cents
 * @returns {string}
 */
export function formatMoney(cents, format = config.moneyFormat) {
  const value = Math.round(Number(cents) || 0);

  /** @param {number} decimals @param {string} thousands @param {string} decimal */
  const build = (decimals, thousands, decimal) => {
    const fixed = (value / 100).toFixed(decimals);
    const [whole, fraction] = fixed.split('.');
    const grouped = (whole || '0').replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    if (!fraction || /^0+$/.test(fraction)) return grouped;
    return grouped + decimal + fraction;
  };

  const plain = String(format).replace(/<[^>]*>/g, '');

  return plain.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return build(0, ',', '.');
      case 'amount_with_comma_separator':
        return build(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return build(0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return build(2, "'", '.');
      case 'amount_no_decimals_with_space_separator':
        return build(0, ' ', '.');
      case 'amount_with_space_separator':
        return build(2, ' ', ',');
      default:
        return build(2, ',', '.');
    }
  });
}

/**
 * Sends a CRO event to GA4 (through the gtag the Google & YouTube app already loads) and to
 * Shopify's customer events bus so any custom pixel can subscribe. Falls back to a plain
 * dataLayer push when gtag is not on the page. Standard ecommerce events (add_to_cart,
 * begin_checkout, purchase) are left to the installed apps so nothing is double counted.
 *
 * @param {string} name
 * @param {Record<string, unknown>} [params]
 */
export function track(name, params = {}) {
  if (!config.analytics) return;

  const payload = { page_type: config.template, currency: config.currency, ...params };

  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
    } else if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...payload });
    }

    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (error) {
    if (config.debug) console.warn('[MONANI CRO] tracking failed', error);
  }

  if (config.debug) console.log('[MONANI CRO]', name, payload);
}

/** @returns {Promise<{total_price: number, item_count: number} | null>} */
async function fetchCart() {
  try {
    const response = await fetch(config.routes.cart, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Adds one or more variants to the cart and lets Horizon re-render itself.
 *
 * @param {Array<{id: number, quantity?: number}>} items
 * @param {HTMLElement} source - Element the event is dispatched from
 * @returns {Promise<{ok: true, cart: any} | {ok: false, message: string}>}
 */
export async function addToCart(items, source) {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
    .filter(Boolean);

  const body = JSON.stringify({
    items: items.map((item) => ({ id: item.id, quantity: item.quantity || 1 })),
    sections: Array.from(new Set(sectionIds)).join(','),
    sections_url: window.location.pathname,
  });

  let data;
  try {
    const response = await fetch(config.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    data = await response.json();
  } catch (error) {
    console.error('[MONANI CRO] add to cart failed', error);
    return { ok: false, message: 'Something went wrong. Please try again.' };
  }

  if (data?.status) {
    return { ok: false, message: data.description || data.message || 'This item could not be added.' };
  }

  const cart = await fetchCart();
  const quantityAdded = items.reduce((sum, item) => sum + (item.quantity || 1), 0);

  source.dispatchEvent(
    new CartAddEvent(cart ?? undefined, source.id || 'monani-cro', {
      source: 'monani-cro',
      itemCount: cart?.item_count ?? cartState.count + quantityAdded,
      variantId: String(items[0]?.id ?? ''),
      sections: data?.sections,
    })
  );

  return { ok: true, cart };
}

/**
 * Adds (or refreshes) the stylesheet that came with a Section Rendering API response.
 * @param {string} key
 * @param {string} css
 */
function injectSectionStyles(key, css) {
  const id = `mn-section-styles-${key}`;
  let style = document.getElementById(id);

  if (!style) {
    style = document.createElement('style');
    style.id = id;
    document.head.append(style);
  }

  if (style.textContent !== css) style.textContent = css;
}

/**
 * @template {(...args: any[]) => void} T
 * @param {T} fn
 * @param {number} wait
 */
function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timer;
  return (/** @type {Parameters<T>} */ ...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), wait);
  };
}

/* -------------------------------------------------------------------------- */
/* <monani-mini-card>                                                         */
/* -------------------------------------------------------------------------- */

/**
 * @typedef {Object} MiniVariant
 * @property {number} id
 * @property {string[]} o - option values
 * @property {boolean} a - available
 * @property {number} p - price in cents
 * @property {string} t - variant title
 * @property {string | null} i - image url
 */

class MonaniMiniCard extends HTMLElement {
  /** @type {MiniVariant[] | null} */
  #variants = null;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {number | undefined} */
  #resetTimer;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.#refreshOptions();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    window.clearTimeout(this.#resetTimer);
  }

  get variants() {
    if (!this.#variants) {
      try {
        this.#variants = JSON.parse(this.querySelector('[data-mn-variants]')?.textContent || '[]');
      } catch {
        this.#variants = [];
      }
    }
    return this.#variants ?? [];
  }

  /** @returns {Array<HTMLSelectElement | HTMLInputElement>} */
  get optionInputs() {
    return /** @type {Array<HTMLSelectElement | HTMLInputElement>} */ (
      Array.from(this.querySelectorAll('[data-mn-option]'))
    ).sort((a, b) => Number(a.dataset.mnOption) - Number(b.dataset.mnOption));
  }

  /** Current option values in option order. Empty string means "not chosen yet". */
  get selectedValues() {
    return this.optionInputs.map((input) => input.value);
  }

  /** @returns {MiniVariant | undefined} */
  get selectedVariant() {
    const values = this.selectedValues;
    if (values.length === 0) return this.variants[0];
    if (values.some((value) => value === '')) return undefined;
    return this.variants.find((variant) => variant.o.every((value, index) => value === values[index]));
  }

  get trackingData() {
    return {
      context: this.dataset.context || 'upsell',
      position: Number(this.dataset.position) || 1,
      product_id: this.dataset.productId,
      product_title: this.dataset.productTitle,
      price: (Number(this.dataset.productPrice) || 0) / 100,
    };
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;

    event.target.closest('.mn-card__select')?.classList.remove('is-missing');
    this.#setError('');
    this.#refreshOptions();

    // Only a colour (non size) change should swap the thumbnail away from the hero image
    if (!event.target.hasAttribute('data-mn-required')) this.#refreshImage();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-mn-link]')) {
      track('upsell_click', this.trackingData);
      return;
    }

    if (target.closest('[data-mn-add]')) {
      event.preventDefault();
      this.#add();
    }
  };

  /** Disables values that do not exist (or are sold out) for the other chosen options. */
  #refreshOptions() {
    const inputs = this.optionInputs;
    const values = this.selectedValues;

    inputs.forEach((input, index) => {
      if (!(input instanceof HTMLSelectElement)) return;

      for (const option of Array.from(input.options)) {
        if (option.value === '') continue;

        option.disabled = !this.variants.some(
          (variant) =>
            variant.a &&
            variant.o[index] === option.value &&
            variant.o.every((value, i) => i === index || values[i] === '' || values[i] === value)
        );
      }

      // A size that does not exist in the newly chosen colour goes back to the placeholder
      if (input.hasAttribute('data-mn-required') && input.selectedOptions[0]?.disabled) input.value = '';
    });
  }

  /** Swaps the thumbnail to match the chosen colour, when the variant has its own image. */
  #refreshImage() {
    const image = this.querySelector('[data-mn-image]');
    if (!(image instanceof HTMLImageElement)) return;

    const values = this.selectedValues;
    const match = this.variants.find(
      (variant) => variant.i && variant.o.every((value, index) => values[index] === '' || values[index] === value)
    );

    if (match?.i && image.getAttribute('src') !== match.i) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = match.i;
    }
  }

  /** The thumbnail as the customer sees it now: hero image, or the colour they switched to. */
  #currentImage() {
    const image = this.querySelector('[data-mn-image]');
    return image instanceof HTMLImageElement ? image.currentSrc || image.src : '';
  }

  /** @param {string} message */
  #setError(message) {
    const error = this.querySelector('[data-mn-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = message === '';
  }

  #promptForMissingOption() {
    const missing = this.optionInputs.find((input) => input.value === '');
    const wrapper = missing?.closest('.mn-card__select');
    if (!missing || !wrapper) return;

    wrapper.classList.remove('is-missing');
    // Restart the nudge animation
    void (/** @type {HTMLElement} */ (wrapper).offsetWidth);
    wrapper.classList.add('is-missing');
    missing.focus({ preventScroll: true });
  }

  async #add() {
    if (this.getAttribute('aria-busy') === 'true') return;

    const variant = this.selectedVariant;
    if (!variant) {
      this.#promptForMissingOption();
      return;
    }

    if (!variant.a) {
      this.#setError('That option is sold out.');
      return;
    }

    if (this.dataset.mode === 'pack') {
      this.dispatchEvent(
        new CustomEvent('monani:pack-select', {
          bubbles: true,
          detail: {
            variantId: variant.id,
            variantTitle: variant.t,
            price: variant.p,
            image: this.#currentImage() || variant.i || '',
            productId: this.dataset.productId,
            productTitle: (this.dataset.productTitle || '').split(':')[0]?.trim(),
          },
        })
      );
      return;
    }

    const label = this.querySelector('[data-mn-add-label]');
    const originalLabel = label?.textContent ?? '';

    this.setAttribute('aria-busy', 'true');
    this.#setError('');

    const result = await addToCart([{ id: variant.id, quantity: 1 }], this);

    this.removeAttribute('aria-busy');

    if (!result.ok) {
      this.#setError(result.message);
      return;
    }

    track('upsell_add_to_cart', {
      ...this.trackingData,
      variant_id: variant.id,
      variant_title: variant.t,
      value: variant.p / 100,
      cart_total: (result.cart?.total_price ?? cartState.total) / 100,
    });

    if (label) {
      this.classList.add('is-added');
      label.textContent = window.Theme?.translations?.added || 'Added';
      this.#resetTimer = window.setTimeout(() => {
        this.classList.remove('is-added');
        label.textContent = originalLabel;
      }, 1600);
    }
  }
}

/* -------------------------------------------------------------------------- */
/* <monani-recs>                                                              */
/* -------------------------------------------------------------------------- */

class MonaniRecs extends HTMLElement {
  static observedAttributes = ['data-cart-sig'];

  /** @type {AbortController | null} */
  #fetchAbort = null;
  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;
  /** @type {IntersectionObserver | null} */
  #impressionObserver = null;
  #hasLoaded = false;
  #isVisible = false;
  #lastImpression = '';
  #connected = false;

  #reload = debounce(() => this.load(), 140);

  connectedCallback() {
    this.#connected = true;

    if (this.dataset.load === 'idle') {
      const start = () => this.load();
      'requestIdleCallback' in window ? window.requestIdleCallback(start, { timeout: 2500 }) : window.setTimeout(start, 600);
      return;
    }

    // 'visible': wait until the element is on screen (the cart drawer has opened)
    this.#visibilityObserver = new IntersectionObserver((entries) => {
      this.#isVisible = entries.some((entry) => entry.isIntersecting);
      if (this.#isVisible && !this.#hasLoaded) this.load();
    });
    this.#visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    this.#connected = false;
    this.#fetchAbort?.abort();
    this.#visibilityObserver?.disconnect();
    this.#impressionObserver?.disconnect();
  }

  /** Horizon morphs the new data-cart-sig onto this element after every cart change. */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (!this.#connected || oldValue === null || oldValue === newValue) return;
    if (this.#hasLoaded || this.#isVisible) this.#reload();
  }

  get target() {
    return this.querySelector('[data-mn-recs-target]');
  }

  get limit() {
    const desktop = Number(this.dataset.limit) || config.limitDesktop;
    const mobile = Number(this.dataset.limitMobile) || config.limitMobile;
    return mobileQuery.matches ? mobile : desktop;
  }

  /** @param {boolean} withRecommendations */
  #buildUrl(withRecommendations) {
    const section = this.dataset.section || 'monani-cart-upsells';
    const productId = this.dataset.productId;

    if (withRecommendations && productId) {
      const url = new URL(config.routes.recommendations, window.location.origin);
      url.searchParams.set('section_id', section);
      url.searchParams.set('product_id', productId);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', 'related');
      return url.toString();
    }

    const url = new URL(config.routes.root, window.location.origin);
    url.searchParams.set('section_id', section);
    return url.toString();
  }

  async load() {
    const target = this.target;
    if (!target) return;

    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();
    const { signal } = this.#fetchAbort;

    /** @type {string | null} */
    let html = null;

    for (const withRecommendations of [true, false]) {
      try {
        const response = await fetch(this.#buildUrl(withRecommendations), { signal });
        if (response.ok) {
          html = await response.text();
          break;
        }
      } catch (error) {
        if (signal.aborted) return;
      }
      if (!this.dataset.productId) break;
    }

    if (signal.aborted) return;
    this.#hasLoaded = true;

    const parsed = html ? new DOMParser().parseFromString(html, 'text/html') : null;
    const list = parsed?.querySelector('[data-mn-recs-list]') ?? null;

    // Shopify only ships the CSS for what was rendered with the page. Section Rendering API
    // responses carry their own stylesheet, so add it once before the cards go in.
    const stylesheet = parsed?.querySelector('style[data-section-stylesheet]');
    if (stylesheet?.textContent) injectSectionStyles(this.dataset.section || 'monani-recs', stylesheet.textContent);
    const items = list ? Array.from(list.querySelectorAll('.mn-recs__item')) : [];

    // On a product page never recommend the product being viewed
    const currentProductId = this.dataset.context === 'pdp_cross_sell' ? this.dataset.productId : null;
    const kept = items
      .filter((item) => item.querySelector('monani-mini-card')?.getAttribute('data-product-id') !== currentProductId)
      .slice(0, this.limit);

    target.removeAttribute('aria-busy');
    this.style.setProperty('--mn-recs-min-height', '0px');

    if (!list || kept.length === 0) {
      target.replaceChildren();
      target.setAttribute('data-empty', '');
      if (this.dataset.context === 'pdp_cross_sell') this.hidden = true;
      return;
    }

    kept.forEach((item, index) => {
      const card = item.querySelector('monani-mini-card');
      card?.setAttribute('data-position', String(index + 1));
      if (this.dataset.context) card?.setAttribute('data-context', this.dataset.context);
    });

    list.replaceChildren(...kept);
    target.removeAttribute('data-empty');
    target.replaceChildren(document.importNode(list, true));
    this.hidden = false;

    this.#watchImpression();
  }

  /** Fires upsell_impression once per distinct set of products, when at least half is on screen. */
  #watchImpression() {
    const target = this.target;
    if (!target) return;

    // In the drawer the pinned summary covers the bottom of the scroll area: do not count that as seen
    const root = this.closest('.cart-drawer__content');
    const summary = root?.querySelector('.cart-drawer__summary');
    const isPinned = summary instanceof HTMLElement && getComputedStyle(summary).position === 'sticky';
    const covered = isPinned ? summary.offsetHeight : 0;

    this.#impressionObserver?.disconnect();
    this.#impressionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        const cards = Array.from(target.querySelectorAll('monani-mini-card'));
        const signature = cards.map((card) => card.getAttribute('data-product-id')).join(',');
        if (!signature || signature === this.#lastImpression) return;

        this.#lastImpression = signature;
        track('upsell_impression', {
          context: this.dataset.context || 'upsell',
          item_count: cards.length,
          product_ids: signature,
          cart_total: cartState.total / 100,
        });
      },
      { threshold: 0.5, root, rootMargin: `0px 0px -${covered}px 0px` }
    );
    this.#impressionObserver.observe(target);
  }
}

/* -------------------------------------------------------------------------- */
/* <monani-ship-note>                                                         */
/* -------------------------------------------------------------------------- */

class MonaniShipNote extends HTMLElement {
  connectedCallback() {
    document.addEventListener('monani:cart-total', this.#update);
  }

  disconnectedCallback() {
    document.removeEventListener('monani:cart-total', this.#update);
  }

  #update = () => {
    const text = this.querySelector('[data-mn-ship-text]');
    if (!text || !config.tier1) return;

    const total = cartState.total;
    const price = Number(this.dataset.price) || 0;
    const remaining = Math.max(config.tier1 - total, 0);

    let state = 'default';
    if (total >= config.tier1) state = 'unlocked';
    else if (total > 0 && price >= remaining) state = 'this';
    else if (total > 0) state = 'away';

    const template =
      {
        default: this.dataset.textDefault,
        this: this.dataset.textThis,
        away: this.dataset.textAway,
        unlocked: this.dataset.textUnlocked,
      }[state] || '';

    this.dataset.state = state;
    text.textContent = template
      .replaceAll('[threshold]', formatMoney(config.tier1))
      .replaceAll('[amount]', formatMoney(remaining));
  };
}

/* -------------------------------------------------------------------------- */
/* <monani-pack-builder>                                                      */
/* -------------------------------------------------------------------------- */

/**
 * @typedef {Object} PackItem
 * @property {number} variantId
 * @property {string} variantTitle
 * @property {number} price
 * @property {string} image
 * @property {string} productId
 * @property {string} productTitle
 */

class MonaniPackBuilder extends HTMLElement {
  /** @type {PackItem[]} */
  #items = [];
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('monani:pack-select', this.#onSelect, { signal });
    this.addEventListener('click', this.#onClick, { signal });

    this.#promoteStartProduct();
    this.#render();

    track('bundle_view', { bundle_size: this.size, bundle_percent: this.percent });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get size() {
    return Number(this.dataset.size) || 3;
  }

  get percent() {
    return Number(this.dataset.percent) || 0;
  }

  get subtotal() {
    return this.#items.reduce((sum, item) => sum + item.price, 0);
  }

  get discounted() {
    return Math.round((this.subtotal * (100 - this.percent)) / 100);
  }

  /** Moves the product the customer came from (?start=handle) to the front of the grid. */
  #promoteStartProduct() {
    const handle = new URLSearchParams(window.location.search).get('start');
    if (!handle) return;

    const card = this.querySelector(`monani-mini-card[data-product-handle="${CSS.escape(handle)}"]`);
    const item = card?.closest('li');
    if (item?.parentElement) item.parentElement.prepend(item);
  }

  /** @param {Event} event */
  #onSelect = (event) => {
    const detail = /** @type {CustomEvent<PackItem>} */ (event).detail;
    if (!detail) return;

    if (this.#items.length >= this.size) {
      this.#announce(this.dataset.textFull || 'Your pack is full.');
      const bar = this.querySelector('[data-mn-pack-bar]');
      bar?.classList.remove('is-full-nudge');
      void (/** @type {HTMLElement} */ (bar)?.offsetWidth);
      bar?.classList.add('is-full-nudge');
      return;
    }

    this.#items.push(detail);
    this.#render();
    this.#announce(`${detail.productTitle} ${detail.variantTitle} added. ${this.#items.length} of ${this.size}.`);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const remove = target.closest('[data-mn-pack-remove]');
    if (remove instanceof HTMLElement) {
      this.#items.splice(Number(remove.dataset.index), 1);
      this.#render();
      return;
    }

    if (target.closest('[data-mn-pack-submit]')) this.#submit();
  };

  /** @param {string} message */
  #announce(message) {
    const live = this.querySelector('[data-mn-pack-live]');
    if (live) live.textContent = message;
  }

  #render() {
    const slots = this.querySelector('[data-mn-pack-slots]');
    const price = this.querySelector('[data-mn-pack-price]');
    const was = this.querySelector('[data-mn-pack-was]');
    const submit = this.querySelector('[data-mn-pack-submit]');
    const label = this.querySelector('[data-mn-pack-submit-label]');
    const isFull = this.#items.length >= this.size;
    const remaining = this.size - this.#items.length;

    if (slots) {
      const fragment = document.createDocumentFragment();

      for (let index = 0; index < this.size; index++) {
        const item = this.#items[index];
        const slot = document.createElement('li');
        slot.className = `mn-pack__slot${item ? ' is-filled' : ''}`;

        if (item) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = '';
          image.width = 56;
          image.height = 56;
          image.loading = 'lazy';

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'mn-pack__remove';
          remove.dataset.mnPackRemove = '';
          remove.dataset.index = String(index);
          remove.setAttribute('aria-label', `Remove ${item.productTitle} ${item.variantTitle}`);
          remove.textContent = '×';

          const caption = document.createElement('span');
          caption.className = 'mn-pack__slot-caption';
          caption.textContent = item.variantTitle;

          slot.append(image, remove, caption);
        } else {
          const number = document.createElement('span');
          number.className = 'mn-pack__slot-number';
          number.textContent = String(index + 1);
          slot.append(number);
        }

        fragment.append(slot);
      }

      slots.replaceChildren(fragment);
    }

    if (price) price.textContent = this.#items.length ? formatMoney(isFull ? this.discounted : this.subtotal) : '';
    if (was instanceof HTMLElement) {
      was.textContent = isFull && this.percent > 0 ? formatMoney(this.subtotal) : '';
      was.hidden = !(isFull && this.percent > 0);
    }

    if (submit instanceof HTMLButtonElement) submit.disabled = !isFull;
    if (label) {
      const template = isFull ? this.dataset.textSubmit || 'Add pack to cart' : this.dataset.textPick || 'Pick [count] more';
      label.textContent = template.replaceAll('[count]', String(remaining));
    }
  }

  async #submit() {
    const submit = this.querySelector('[data-mn-pack-submit]');
    if (!(submit instanceof HTMLButtonElement) || submit.disabled || this.#items.length < this.size) return;

    /** @type {Map<number, number>} */
    const quantities = new Map();
    for (const item of this.#items) quantities.set(item.variantId, (quantities.get(item.variantId) || 0) + 1);

    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');

    const result = await addToCart(
      Array.from(quantities, ([id, quantity]) => ({ id, quantity })),
      this
    );

    submit.removeAttribute('aria-busy');

    if (!result.ok) {
      submit.disabled = false;
      this.#announce(result.message);
      const error = this.querySelector('[data-mn-pack-error]');
      if (error instanceof HTMLElement) {
        error.textContent = result.message;
        error.hidden = false;
      }
      return;
    }

    track('bundle_add_to_cart', {
      bundle_size: this.size,
      bundle_percent: this.percent,
      value: this.discounted / 100,
      full_value: this.subtotal / 100,
      variant_ids: this.#items.map((item) => item.variantId).join(','),
      product_ids: this.#items.map((item) => item.productId).join(','),
    });

    const error = this.querySelector('[data-mn-pack-error]');
    if (error instanceof HTMLElement) error.hidden = true;

    this.#items = [];
    this.#render();
    this.#announce(this.dataset.textAdded || 'Pack added to your cart.');

    // Stores using the cart page instead of the drawer go straight to the cart
    if (!document.querySelector('cart-drawer-component')) window.location.href = config.routes.cart.replace(/\.js$/, '');
  }
}

/* -------------------------------------------------------------------------- */
/* Register elements                                                          */
/* -------------------------------------------------------------------------- */

/** @type {Array<[string, CustomElementConstructor]>} */
const elements = [
  ['monani-mini-card', MonaniMiniCard],
  ['monani-recs', MonaniRecs],
  ['monani-ship-note', MonaniShipNote],
  ['monani-pack-builder', MonaniPackBuilder],
];

for (const [name, constructor] of elements) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}

/* -------------------------------------------------------------------------- */
/* Global listeners (registered once: ES modules only evaluate one time)      */
/* -------------------------------------------------------------------------- */

/**
 * Keeps cartState current and fires threshold events when the cart crosses a tier.
 * @param {number} total
 * @param {number} count
 */
function applyCartTotals(total, count) {
  const previous = cartState.total;
  cartState.total = total;
  cartState.count = count;

  if (config.shipEligible && config.tier1 > 0 && previous < config.tier1 && total >= config.tier1) {
    track('shipping_threshold_reached', { threshold: config.tier1 / 100, cart_total: total / 100, item_count: count });
  }

  [config.tier2, config.tier3].forEach((threshold, index) => {
    if (threshold > 0 && previous < threshold && total >= threshold) {
      track('reward_tier_reached', { tier: index + 2, threshold: threshold / 100, cart_total: total / 100 });
    }
  });

  document.dispatchEvent(new CustomEvent('monani:cart-total', { detail: { total, count } }));
}

document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail || {};
  const resource = detail.resource;

  // quick_add: a successful add from Horizon's quick add modal or a product card
  if (detail.data?.source === 'product-form-component' && !detail.data?.didError && event.target instanceof Element) {
    if (event.target.closest('#quick-add-dialog, quick-add-dialog, product-card')) {
      track('quick_add', {
        product_id: detail.data.productId,
        variant_id: detail.sourceId,
        quantity: detail.data.itemCount,
      });
    }
  }

  if (resource && typeof resource.total_price === 'number') {
    applyCartTotals(resource.total_price, resource.item_count ?? cartState.count);
    return;
  }

  const cart = await fetchCart();
  if (cart) applyCartTotals(cart.total_price, cart.item_count);
});

// dialog:open does not bubble, so listen during the capture phase
document.addEventListener(
  'dialog:open',
  (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('cart-drawer-component')) return;
    track('cart_drawer_open', { cart_total: cartState.total / 100, item_count: cartState.count });
  },
  true
);

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  // Checkout button in the drawer or on the cart page
  if (target.closest('button[name="checkout"], .cart__checkout-button')) {
    track('checkout_click', {
      context: target.closest('cart-drawer-component') ? 'cart_drawer' : 'cart_page',
      cart_total: cartState.total / 100,
      item_count: cartState.count,
    });
    return;
  }

  // Quick add button on a product card (opens Horizon's quick add modal)
  const quickAddButton = target.closest('.quick-add__button');
  if (quickAddButton) {
    const card = quickAddButton.closest('product-card');
    track('quick_add_open', {
      product_id: card?.getAttribute('data-product-id') || undefined,
      product_url: card?.querySelector('a[href*="/products/"]')?.getAttribute('href') || undefined,
    });
    return;
  }

  // "Build your rotation" cells
  const rotationLink = target.closest('[data-mn-rotation-link]');
  if (rotationLink instanceof HTMLAnchorElement) {
    track('upsell_click', { context: 'rotation_module', target: rotationLink.dataset.mnRotationLink });

    const hash = rotationLink.getAttribute('href') || '';
    if (hash.startsWith('#')) {
      const destination = document.getElementById(hash.slice(1));
      if (destination && !destination.hidden) {
        event.preventDefault();
        destination.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'center',
        });
      }
    }
    return;
  }

  // Bundle nudge link in the cart
  if (target.closest('[data-mn-bundle-link]')) {
    track('upsell_click', { context: 'cart_bundle_nudge', target: 'bundle' });
    return;
  }

  // One tap add for an unlocked reward gift
  const gift = target.closest('[data-mn-gift]');
  if (gift instanceof HTMLButtonElement && gift.getAttribute('aria-busy') !== 'true') {
    gift.setAttribute('aria-busy', 'true');
    addToCart([{ id: Number(gift.dataset.variantId), quantity: 1 }], gift).then((result) => {
      gift.removeAttribute('aria-busy');
      if (!result.ok) return;
      track('upsell_add_to_cart', {
        context: 'reward_gift',
        product_id: gift.dataset.productId,
        product_title: gift.dataset.productTitle,
        variant_id: gift.dataset.variantId,
      });
    });
  }
});
